import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { BarChart3, ArrowRight, Calendar, Building2, Lock } from "lucide-react";
import type { Assessment } from "@shared/schema";

function formatStatus(status: string): string {
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatPeriod(
  start: Date | string | null,
  end: Date | string | null,
): string {
  if (!start && !end) return "—";
  const fmt = (d: Date | string) =>
    new Date(d).toLocaleDateString(undefined, {
      month: "short",
      year: "numeric",
    });
  if (start && end) return `${fmt(start)} – ${fmt(end)}`;
  if (start) return fmt(start);
  return fmt(end!);
}

export default function Reports() {
  const [, setLocation] = useLocation();

  const {
    data: assessments = [],
    isLoading,
    isError,
  } = useQuery<Assessment[]>({
    queryKey: ["assessments"],
    queryFn: async () => {
      const res = await fetch("/api/assessments", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch assessments");
      return res.json();
    },
  });

  const reports = assessments
    .filter((a) => a.locked)
    .sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return tb - ta;
    });

  return (
    <div className="min-h-full bg-slate-50/60">
      {/* Page header */}
      <div className="relative overflow-hidden bg-white border-b border-slate-200">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 py-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <BarChart3 className="h-5 w-5 text-primary" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">
              Reports
            </h1>
          </div>
          <p className="ml-[52px] text-sm text-slate-400">
            {!isLoading && reports.length > 0
              ? `${reports.length} report${reports.length === 1 ? "" : "s"} available`
              : "Locked assessments appear here as finalized reports"}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <p className="text-sm text-slate-400">{"Error"}</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 mb-4">
              <Lock className="h-7 w-7 text-slate-400" />
            </div>
            <p className="text-sm font-medium text-slate-600 mb-1">
              No reports yet
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reports.map((report) => (
              <button
                key={report.id}
                className="group relative text-left bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-primary/30 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20"
                onClick={() => setLocation(`/report/${report.id}`)}
              >
                {/* Year badge */}
                {report.reportingYear && (
                  <span className="absolute top-4 right-4 text-xs font-bold text-primary bg-primary/10 rounded-full px-2.5 py-0.5">
                    {report.reportingYear}
                  </span>
                )}

                {/* Report name */}
                <h3 className="text-base font-semibold text-slate-900 pr-16 mb-1 line-clamp-1 group-hover:text-primary transition-colors duration-150">
                  {report.name}
                </h3>

                {/* Organization */}
                {report.organizationName && (
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-3">
                    <Building2 className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{report.organizationName}</span>
                  </div>
                )}

                <div className="border-t border-slate-100 my-3" />

                {/* Footer row */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-xs text-slate-400">
                    <Calendar className="h-3.5 w-3.5 shrink-0" />
                    {formatPeriod(report.periodStart, report.periodEnd)}
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-emerald-600 bg-emerald-50 rounded-full px-2.5 py-0.5 whitespace-nowrap">
                      {formatStatus(report.status ?? "—")}
                    </span>
                    <span className="flex items-center gap-1 text-xs font-bold text-primary whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                      View Report
                      <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
